import { useState } from "react";
import { Check, Award, ChevronDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import { resolveIcon } from "../shared/iconMap";
import PriceGate from "../shared/PriceGate";
import { AirlineSlideHead } from "../shared/AirlineSlideRenderers";

const fmt = (n) =>
  "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const SlideSQCommercials = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const brand = airline.brandColor || "#F2A900";
  const c = airline?.content?.commercials || {};
  const categories = c.categories || [];
  const keyStrengths = c.keyStrengths || [];
  const termsAndConditions = c.termsAndConditions || [];
  const grandTotal = categories.reduce((s, x) => s + Number(x.annual || 0), 0);
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || null);
  const [showStrengths, setShowStrengths] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <PriceGate accentColor={brand} isDark={isDark}>
      {({ revealed, Toggle, lockedNode }) => (
        <div>
          <AirlineSlideHead
            eyebrow="Commercials"
            title="Five categories,"
            accentLine="one annual proposal."
            airline={airline}
            accent={brand}
            action={Toggle}
          />
          <p className={`k-lead max-w-[54ch] ${isDark ? "text-white/70" : "text-black/60"}`}>
            {c.subheading}
          </p>

          {!revealed ? (
            <div className="mt-10">{lockedNode}</div>
          ) : (
            <>
              <div className={`mt-12 pt-8 border-t flex items-baseline justify-between gap-6 ${isDark ? "border-white/10" : "border-black/10"}`} data-testid="grand-total-card">
                <div>
                  <div className="k-eyebrow" style={{ color: brand }}>{c.grandTotalLabel}</div>
                  <div className={`mt-2 text-[14px] italic max-w-[36ch] ${isDark ? "text-white/50" : "text-black/50"}`}>{c.grandTotalNote}</div>
                </div>
                <div className="text-[64px] font-semibold tracking-tight leading-none" style={{ color: brand }}>
                  {fmt(grandTotal)}
                </div>
              </div>

              <div className={`mt-10 grid grid-cols-${categories.length} gap-4`} style={{ gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))` }}>
                {categories.map((cat) => {
                  const Icon = resolveIcon(cat.iconName);
                  const isActive = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setActiveCategory(isActive ? null : cat.id)}
                      data-testid={`category-${cat.id}`}
                      className={`text-left rounded-2xl p-5 border transition-all ${
                        isDark
                          ? isActive ? "bg-white/[0.06] border-[#F2A900]" : "bg-white/[0.03] border-white/10 hover:bg-white/[0.06]"
                          : isActive ? "bg-white border-[#F2A900]" : "bg-white border-black/10 hover:border-black/25"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${brand}22` }}>
                        <Icon className="w-4 h-4" style={{ color: brand }} />
                      </div>
                      <div className={`text-[13px] mb-1 ${isDark ? "text-white/60" : "text-black/55"}`}>{cat.label}</div>
                      <div className={`text-[24px] font-semibold tracking-tight ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{fmt(cat.annual)}</div>
                      <div className="k-eyebrow mt-1" style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>per year</div>
                    </button>
                  );
                })}
              </div>

              {activeCategory && (() => {
                const cat = categories.find((x) => x.id === activeCategory);
                if (!cat) return null;
                return (
                  <div
                    className={`mt-6 rounded-2xl border p-6 ${isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10"}`}
                    data-testid={`category-detail-${cat.id}`}
                  >
                    <p className={`text-[15px] leading-relaxed mb-4 ${isDark ? "text-white/75" : "text-black/70"}`}>{cat.note}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.tags.map((tag, i) => (
                        <span key={i} className="text-[12px] px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${brand}18`, color: brand }}>{tag}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div className="mt-10 grid grid-cols-2 gap-6">
                <Collapse
                  isDark={isDark}
                  brand={brand}
                  open={showStrengths}
                  onToggle={() => setShowStrengths((v) => !v)}
                  testid="toggle-strengths"
                  eyebrow={
                    <>
                      <Award className="w-4 h-4" style={{ color: brand }} />
                      <span>{keyStrengths.length} Key strengths</span>
                    </>
                  }
                >
                  <ul className="space-y-2">
                    {keyStrengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] leading-snug">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: brand }} />
                        <span className={`${isDark ? "text-white/75" : "text-black/70"}`}>{s}</span>
                      </li>
                    ))}
                  </ul>
                </Collapse>
                <Collapse
                  isDark={isDark}
                  brand={brand}
                  open={showTerms}
                  onToggle={() => setShowTerms((v) => !v)}
                  testid="toggle-terms"
                  eyebrow={<span>Summary of terms & conditions</span>}
                >
                  <dl className="space-y-1.5 text-[13px]">
                    {termsAndConditions.map((t, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <dt className={`font-semibold min-w-[120px] ${isDark ? "text-white/70" : "text-black/70"}`}>{t.label}</dt>
                        <dd className={`${isDark ? "text-white/55" : "text-black/55"}`}>{t.value}</dd>
                      </div>
                    ))}
                  </dl>
                </Collapse>
              </div>
            </>
          )}
        </div>
      )}
    </PriceGate>
  );
};

const Collapse = ({ isDark, brand, open, onToggle, testid, eyebrow, children }) => (
  <div className={`rounded-2xl border ${isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10"}`}>
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="w-full flex items-center justify-between gap-2 px-5 py-4 text-left"
    >
      <div className={`flex items-center gap-2 text-[14px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
        {eyebrow}
      </div>
      <ChevronDown
        className={`w-4 h-4 transition-transform ${isDark ? "text-white/45" : "text-black/45"}`}
        style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
      />
    </button>
    {open && (
      <div className={`px-5 pb-5 border-t pt-4 ${isDark ? "border-white/10" : "border-black/10"}`}>
        {children}
      </div>
    )}
  </div>
);

export default SlideSQCommercials;
